import { useEffect, useMemo } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CTASection from '../home/components/CTASection';
import LeistungenHero from './components/LeistungenHero';
import LeistungenNav from './components/LeistungenNav';
import LeistungenDetail from './components/LeistungenDetail';
import { leistungen } from '../../mocks/leistungen';
import SEO from '../../components/feature/SEO';
import JsonLd from '../../components/feature/JsonLd';
import { useState, useCallback } from 'react';

export default function LeistungenPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://igm-baudienste.com';
  const [activeId, setActiveId] = useState(leistungen[0].id);
  const [navTriggered, setNavTriggered] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNavSelect = (id: string) => {
    setNavTriggered(true);
    setActiveId(id);
    setTimeout(() => setNavTriggered(false), 800);
  };

  const handleVisible = useCallback(
    (id: string) => {
      if (!navTriggered) setActiveId(id);
    },
    [navTriggered]
  );

  const jsonLdData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/leistungen`,
        'url': `${siteUrl}/leistungen`,
        'name': 'Leistungen | IGM Facility Service Ratingen',
        'description': 'Entdecken Sie das umfangreiche Leistungsangebot von IGM Facility Service: Gebäudereinigung, Gartenpflege, Malerarbeiten, Winterdienst und mehr in Ratingen & NRW.',
        'isPartOf': { '@id': `${siteUrl}/#website` },
      },
      {
        '@type': 'ItemList',
        'name': 'Leistungen IGM Facility Service',
        'url': `${siteUrl}/leistungen`,
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'Geb\u00e4udereinigung' },
          { '@type': 'ListItem', 'position': 2, 'name': 'Hausmeisterservice' },
          { '@type': 'ListItem', 'position': 3, 'name': 'Sanierung & Renovierung' },
          { '@type': 'ListItem', 'position': 4, 'name': 'Trockenbau' },
          { '@type': 'ListItem', 'position': 5, 'name': 'Fassade & Au\u00dfenw\u00e4nde' },
          { '@type': 'ListItem', 'position': 6, 'name': 'Bodenbeläge' },
          { '@type': 'ListItem', 'position': 7, 'name': 'Malerarbeiten' },
          { '@type': 'ListItem', 'position': 8, 'name': 'Garten- & Landschaftsbau' },
          { '@type': 'ListItem', 'position': 9, 'name': 'Bauzeichnungen' },
        ],
      },
    ],
  }), [siteUrl]);

  const breadcrumbData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': siteUrl,
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': 'Leistungen',
        'item': `${siteUrl}/leistungen`,
      },
    ],
  }), [siteUrl]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="Leistungen | Gebäudereinigung & Facility Management – IGM Ratingen"
        description="Entdecken Sie das umfangreiche Leistungsangebot von IGM Facility Service: Gebäudereinigung, Gartenpflege, Malerarbeiten, Winterdienst und mehr in Ratingen & NRW."
        keywords="Gebäudereinigung Ratingen, Gartenpflege NRW, Malerarbeiten Ratingen, Winterdienst NRW"
        canonical={`${siteUrl}/leistungen`}
      />
      <JsonLd data={jsonLdData} id="json-ld-leistungen" />
      <JsonLd data={breadcrumbData} id="json-ld-leistungen-breadcrumb" />
      <Navbar />
      <LeistungenHero />

      {/* Hinweis-Banner: Reinigung & Hausmeisterservice */}
      <div className="bg-[#0D2B4E] py-4">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-sparkling-2-line text-[#C8A96E] text-xl" />
              </div>
              <p className="text-white text-sm">
                <span className="font-semibold">Gebäudereinigung & Hausmeisterservice</span>
                <span className="text-white/70"> – diese Leistungen finden Sie auf unserer Facility-Service-Website.</span>
              </p>
            </div>
            <a
              href="https://www.igmfacilityservice.de"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-[#C8A96E] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#b8996e] transition-colors whitespace-nowrap cursor-pointer"
            >
              igmfacilityservice.de
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-external-link-line" />
              </div>
            </a>
          </div>
        </div>
      </div>

      <LeistungenNav activeId={activeId} onSelect={handleNavSelect} />
      <LeistungenDetail activeId={activeId} onVisible={handleVisible} />
      <CTASection />
      <Footer />

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-24 right-4 sm:bottom-8 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 bg-[#0D2B4E] text-white rounded-full hover:bg-[#0a2240] transition-all flex items-center justify-center z-40 cursor-pointer"
        aria-label="Nach oben"
      >
        <i className="ri-arrow-up-line text-xl sm:text-2xl" />
      </button>
    </div>
  );
}
